import type { Distance } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { Math as MathExpr } from "@pdfking/quantity"
import { Ast, CM, MM } from "@pdfking/quantity"

const n = (v: number) => Ast.Num([v as Distance], 0)
const d = (v: Distance) => Ast.Num([v], 1)

// The symbols a grow's dimensions are written in: the closed page as it
// opens at the side — `width` along the opening, `height` across it, turned
// when it opens at the top —, the spine or the box's `depth`, and the
// thickness of the sheet.
export const DIM_WIDTH: MathExpr = Ast.Sym("width")
export const DIM_HEIGHT: MathExpr = Ast.Sym("height")
export const DIM_DEPTH: MathExpr = Ast.Sym("depth")
export const DIM_SHEET_DEPTH: MathExpr = Ast.Sym("sheet_depth")

export const DIM_CONST = (v: Distance): MathExpr => d(v)

/** A plain number, no length: a factor. */
export const DIM_NUM = (v: number): MathExpr => n(v)

/** `v` millimetres. */
export const DIM_MM = (v: number): MathExpr => d(MM(v))

/** `a` times `factor`. */
export const DIM_TIMES = (a: MathExpr, factor: number): MathExpr => Ast.Mul(a, n(factor))

export const DIM_ZERO: MathExpr = d(ZERO_DISTANCE)

// `factor` times a dimension: none, it, or a share of it.
const factor_of =
  (dimension: MathExpr) =>
  (factor: number): MathExpr =>
    factor === 0 ? d(ZERO_DISTANCE) : factor === 1 ? dimension : Ast.Mul(dimension, n(factor))

/** `factor` times the page's width: along the opening. */
export const DIM_WIDTH_FACTOR = factor_of(DIM_WIDTH)

/** `factor` times the page's height: across the opening. */
export const DIM_HEIGHT_FACTOR = factor_of(DIM_HEIGHT)

// Min(Max(width * 0.5, 10cm), width - 1cm) — cover flap fold-back size.
export const DIM_FLAP_SIZE: MathExpr = Ast.Min(
  Ast.Max(Ast.Mul(DIM_WIDTH, n(0.5)), d(CM(10))),
  Ast.Sub(DIM_WIDTH, d(CM(1)))
)
